/**
 * Pure helpers for the Notebook's UI model.
 *
 * Firestore stores `notebooks/{uid}/meta/main` (section list with `pageIds`) and
 * `notebooks/{uid}/pages/{id}` (page content). The UI works with a joined
 * `sections[]` array where each section carries its `pages[]`.
 */

/** Name used for a page listed in meta whose document has not been loaded (yet). */
export const PLACEHOLDER_PAGE_NAME = 'Loading...';

function toUiPage(pageId, page) {
  const stickyList = page?.stickyImages ?? page?.stickyNotes ?? [];
  return {
    id: page.id ?? pageId,
    name: page.name || 'Untitled',
    content: page.content || '',
    color: page.color ?? null,
    stickyNotes: page.stickyNotes ?? stickyList,
    stickyImages: stickyList,
  };
}

function toPlaceholderPage(pageId) {
  return {
    id: pageId,
    name: PLACEHOLDER_PAGE_NAME,
    content: '',
    color: null,
    stickyNotes: [],
    stickyImages: [],
    // UI-only flag: never persisted (see NotebookPage's save diff).
    isPlaceholder: true,
  };
}

/**
 * Convert notebook meta + pages map into UI-friendly sections structure.
 * Pages listed in meta without a loaded document become placeholders
 * (`isPlaceholder: true`).
 */
export function buildSectionsFromMetaAndPages(meta, pages) {
  if (!meta || !Array.isArray(meta.sections)) return [];

  const pagesMap = pages && typeof pages === 'object' ? pages : {};

  return meta.sections.map(sec => ({
    id: sec.id,
    name: sec.name || 'Untitled',
    color: sec.color || '#3b82f6',
    collapsed: sec.collapsed ?? false,
    isFolder: sec.isFolder ?? false,
    parentId: sec.parentId ?? null,
    pages: (sec.pageIds || []).map(pageId => {
      const page = pagesMap[pageId];
      return page ? toUiPage(pageId, page) : toPlaceholderPage(pageId);
    })
  }));
}

export function isPlaceholderPage(page) {
  return Boolean(page?.isPlaceholder);
}

/** Every page id referenced by meta. */
export function getMetaPageIds(meta) {
  const ids = new Set();
  if (!meta || !Array.isArray(meta.sections)) return ids;
  meta.sections.forEach((section) => {
    (section?.pageIds || []).forEach((pageId) => {
      if (pageId) ids.add(pageId);
    });
  });
  return ids;
}

/**
 * Replace placeholder pages with their loaded documents from `pagesMap`.
 * Only placeholders are touched, so local edits are never overwritten.
 * Returns the same array when nothing changed.
 */
export function fillPlaceholderPages(sections, pagesMap) {
  if (!Array.isArray(sections) || !pagesMap) return sections;
  let changed = false;
  const next = sections.map((section) => {
    let sectionChanged = false;
    const pages = (section.pages || []).map((page) => {
      if (!isPlaceholderPage(page) || !pagesMap[page.id]) return page;
      sectionChanged = true;
      return toUiPage(page.id, pagesMap[page.id]);
    });
    if (!sectionChanged) return section;
    changed = true;
    return { ...section, pages };
  });
  return changed ? next : sections;
}

/** The meta shape persisted for each section (see `updateMeta`). */
export function toMetaSections(sections) {
  return (sections || []).map((s) => ({
    id: s.id,
    name: s.name,
    color: s.color,
    collapsed: s.collapsed,
    isFolder: s.isFolder,
    parentId: s.parentId,
    pageIds: (s.pages || []).map((p) => p.id),
  }));
}

/**
 * Remove a section or folder. Sections inside a deleted folder move to the top
 * level (parentId: null) instead of being stranded under a missing parent.
 * Returns the next sections and the ids of pages that belonged to the removed item.
 */
export function removeSectionReparentingChildren(sections, id) {
  const list = Array.isArray(sections) ? sections : [];
  const target = list.find((section) => section.id === id);
  if (!target) return { sections: list, removedPageIds: [], movedSectionIds: [] };

  const movedSectionIds = [];
  const next = list
    .filter((section) => section.id !== id)
    .map((section) => {
      if (target.isFolder && section.parentId === id) {
        movedSectionIds.push(section.id);
        return { ...section, parentId: null };
      }
      return section;
    });

  return {
    sections: next,
    removedPageIds: (target.pages || []).map((page) => page.id),
    movedSectionIds,
  };
}

/**
 * Move a page within or between sections. `toIndex` is the insertion index in
 * the target section as rendered before the move; `null` appends. Moving within
 * the same section with `toIndex: null` is a no-op.
 */
export function movePage(sections, { fromSectionId, pageId, toSectionId, toIndex = null }) {
  const list = Array.isArray(sections) ? sections : [];
  const source = list.find((section) => section.id === fromSectionId);
  const fromIndex = source ? (source.pages || []).findIndex((page) => page.id === pageId) : -1;
  if (fromIndex < 0 || !list.some((section) => section.id === toSectionId)) return list;
  const page = source.pages[fromIndex];

  if (fromSectionId === toSectionId) {
    if (toIndex === null || toIndex === fromIndex || toIndex === fromIndex + 1) return list;
    return list.map((section) => {
      if (section.id !== toSectionId) return section;
      const pages = [...section.pages];
      pages.splice(fromIndex, 1);
      pages.splice(fromIndex < toIndex ? toIndex - 1 : toIndex, 0, page);
      return { ...section, pages };
    });
  }

  return list.map((section) => {
    if (section.id === fromSectionId) {
      return { ...section, pages: section.pages.filter((candidate) => candidate.id !== pageId) };
    }
    if (section.id === toSectionId) {
      const pages = [...(section.pages || [])];
      const index = toIndex === null ? pages.length : Math.max(0, Math.min(toIndex, pages.length));
      pages.splice(index, 0, page);
      return { ...section, pages };
    }
    return section;
  });
}

/** Put a (non-folder) section inside a folder, or at the top level with `folderId: null`. */
export function moveSectionToFolder(sections, sectionId, folderId) {
  const list = Array.isArray(sections) ? sections : [];
  const section = list.find((candidate) => candidate.id === sectionId);
  if (!section || section.isFolder || sectionId === folderId) return list;
  const nextParent = folderId || null;
  if ((section.parentId || null) === nextParent) return list;
  return list.map((candidate) => (candidate.id === sectionId ? { ...candidate, parentId: nextParent } : candidate));
}

/**
 * Group sections for the sidebar tree: top-level items in order, each folder
 * with its child sections. A section whose parent folder no longer exists is
 * shown at the top level so it can't disappear.
 * Returns `[{ item, children }]` (children is `[]` for plain sections).
 */
export function buildNotebookTree(sections) {
  const list = Array.isArray(sections) ? sections : [];
  const folderIds = new Set(list.filter((section) => section.isFolder).map((section) => section.id));
  const childrenByFolder = new Map();
  const roots = [];

  list.forEach((section) => {
    if (!section.isFolder && section.parentId && folderIds.has(section.parentId)) {
      if (!childrenByFolder.has(section.parentId)) childrenByFolder.set(section.parentId, []);
      childrenByFolder.get(section.parentId).push(section);
    } else {
      roots.push(section);
    }
  });

  return roots.map((item) => ({
    item,
    children: item.isFolder ? childrenByFolder.get(item.id) || [] : [],
  }));
}

/**
 * Filter sections by a search query over section, folder and page names and
 * page content. Matching sections are expanded; a folder is kept when its name
 * matches (with all its sections) or when any of its sections match.
 */
export function filterNotebookSections(sections, query) {
  const list = Array.isArray(sections) ? sections : [];
  const q = String(query || '').trim().toLowerCase();
  if (!q) return list;

  const includes = (value) => String(value || '').toLowerCase().includes(q);
  const matchingFolderIds = new Set(
    list.filter((section) => section.isFolder && includes(section.name)).map((section) => section.id)
  );

  const kept = new Map();
  list.forEach((section) => {
    if (section.isFolder) return;
    const nameMatch = includes(section.name) || (section.parentId && matchingFolderIds.has(section.parentId));
    const pages = (section.pages || []).filter((page) => includes(page.name) || includes(page.content));
    if (nameMatch || pages.length > 0) {
      kept.set(section.id, { ...section, pages: nameMatch ? section.pages : pages, collapsed: false });
    }
  });

  const parentsWithMatches = new Set(Array.from(kept.values()).map((section) => section.parentId).filter(Boolean));

  return list
    .map((section) => {
      if (section.isFolder) {
        return matchingFolderIds.has(section.id) || parentsWithMatches.has(section.id)
          ? { ...section, collapsed: false }
          : null;
      }
      return kept.get(section.id) || null;
    })
    .filter(Boolean);
}
